import BibleBook from './BibleBook';
import BibleChapter from './BibleChapter';
import BibleVerse from './BibleVerse';
import Testament from './Testament';

interface Verse {
    name: string;
    shortName: string;
    index: number;
    bookIndex: number;
    chapterIndex: number;
    testament: Testament;
}

export default class Bible {

    public static fromVerses(verses: Verse[]): Bible {
        const bible = new Bible();
        for (const verse of verses) {
            bible.addVerse(verse);
        }
        return bible;
    }

    private books = new Map<number, BibleBook>();
    private chapters = new Map<number, BibleChapter>();
    private verses = new Map<number, BibleVerse>();

    public getBooks(): BibleBook[] {
        return Array.from(this.books.values());
    }

    public getChapters(): BibleChapter[] {
        return Array.from(this.chapters.values());
    }

    public getVerses(): BibleVerse[] {
        return Array.from(this.verses.values());
    }

    public getBook(index: number): BibleBook {
        const book = this.books.get(index);
        if (book === undefined) {
            throw new Error('Book not found');
        }
        return book;
    }

    public getChapter(index: number): BibleChapter {
        const chapter = this.chapters.get(index);
        if (chapter === undefined) {
            throw new Error('Chapter not found');
        }
        return chapter;
    }

    public getVerse(index: number): BibleVerse {
        const verse = this.verses.get(index);
        if (verse === undefined) {
            throw new Error('Verse not found');
        }
        return verse;
    }

    public isBookIndex(index: number): boolean {
        return this.books.has(index);
    }

    public isChapterIndex(index: number): boolean {
        return this.chapters.has(index);
    }

    public isVerseIndex(index: number): boolean {
        return this.verses.has(index);
    }

    private addVerse(verse: Verse): void {
        const nameSplitter = new RegExp(/(.+) (.+):(.+)/);

        const nameParts = nameSplitter.exec(verse.name);
        if (nameParts === null || nameParts.length < 4) {
            throw new Error('Invalid verse name');
        }
        const bookName = nameParts[1];
        const chapterNumber = Number(nameParts[2]);
        const verseNumber = Number(nameParts[3]);
        const chapterName = `${bookName} ${chapterNumber}`;
        const verseName = `${bookName} ${chapterNumber}:${verseNumber}`;

        const shortNameParts = nameSplitter.exec(verse.shortName);
        if (shortNameParts === null || shortNameParts.length < 2) {
            throw new Error('Invalid verse short name');
        }
        const shortBookName = shortNameParts[1];
        const shortChapterName = `${shortBookName} ${chapterNumber}`;
        const shortVerseName = `${shortBookName} ${chapterNumber}:${verseNumber}`;

        const book = this.getOrSetBook(verse.bookIndex, bookName, shortBookName, verse.testament);
        const chapter = book.getOrSetChapter(verse.chapterIndex, chapterNumber, chapterName, shortChapterName);
        this.chapters.set(verse.chapterIndex, chapter);
        this.verses.set(verse.index, chapter.getOrSetVerse(verse.index, verseNumber, verseName, shortVerseName));
    }

    private getOrSetBook(index: number, name: string, shortName: string, testament: Testament): BibleBook {
        let book = this.books.get(index);
        if (book === undefined) {
            book = new BibleBook(index, name, shortName, testament);
            this.books.set(index, book);
        }
        return book;
    }
}
